import { Button, Col, Flex, Row, Typography } from "antd";
import queryClient from "api/queryClient";
import {
    useDestroyConnectionSequenceStepMutation,
    useGetConnectionSequenceStepsQuery,
    useStoreConnectionSequenceStepMutation,
    useUpdateConnectionSequenceStepMutation,
} from "api/TenantSlug/ConnectionSequenceStep/hooks";
import { useUpdateIncomingPostbackMutation } from "api/TenantSlug/IncomingPostback/hooks";
import { EditorDrawers } from "components/Drawers/EditorDrawers/EditorDrawers";
import { DrawerLink } from "components/UI/DrawerLink";
import { NestedGeneralFormProvider } from "components/UI/GeneralForm/NestedProvider/NestedGeneralFormProvider";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { usePermission } from "hooks/usePermissions.ts";
import { FC, useState } from "react";
import { useFieldArray, useFormContext } from "react-hook-form";
import { toast } from "react-toastify";
import { ConnectionSequenceStepModelType, PermissionEnum, RequestMethod, RequestPayloadType } from "types/enums";
import { DrawerId, FormId } from "types/models";
import { transformRequestConfig } from "utils/editorTransformRequestConfig";

import { ApiMethodPanel } from "./ApiMethodPanel";
import { FormData, schema } from "./formSchema";
import { IncomingPostbackHeaderPanel } from "./IncomingPostbackHeader/IncomingPostbackHeaderPanel";

import IncomingPostbackDataOptional = App.Data.IncomingPostbackDataOptional;
import ConnectionSequenceStepData = App.Data.ConnectionSequenceStepData;
import StoreConnectionSequenceStepRequest = App.Http.Requests.ConnectionCreator.StoreConnectionSequenceStepRequest;

interface TrackingToolFromEditorFormProps {
    postback: IncomingPostbackDataOptional;
}

export const TrackingToolFromEditorForm: FC<TrackingToolFromEditorFormProps> = ({ postback }) => {
    const { t } = usePaldockTranslation("ui.connection-creator-editor");
    const queryString = `meanings,
    requestConfig.responseHandlers.responseConnectionPropertiesCheck,
    requestConfig.requestConnectionPropertiesUrl,
    requestConfig.requestConnectionPropertiesUrl.requestConnectionPropertyModify,
    requestConfig.requestConnectionProperties,
    requestConfig.requestConnectionProperties.requestConnectionPropertyModify,
    requestConfig.headers,
    requestConfig.headers.requestConnectionPropertyModify,
    requestConfig.responseHandlers.stepConnection,
    requestConfig.responseHandlers.responseConnectionProperties,
    requestConfig.responseHandlers.responseConnectionProperties.requestConnectionPropertyModify`;

    const getConnectionSequenceStepsQuery = useGetConnectionSequenceStepsQuery({
        filter: {
            include: queryString.replace(/\s+/g, ""),
            filter: { incoming_postback_id: postback.id },
        },
    });

    const [deletedIds, setDeletedIds] = useState<Array<number | string>>([]);
    const [lastTempId, setLastTempId] = useState(0);

    const destroyConnectionSequenceStepMutation = useDestroyConnectionSequenceStepMutation();
    const storeConnectionSequenceStepMutation = useStoreConnectionSequenceStepMutation();
    const updateConnectionSequenceStepMutation = useUpdateConnectionSequenceStepMutation();
    const updateIncomingPostbackMutation = useUpdateIncomingPostbackMutation(postback.id);

    const emptyConnectionSequenceStepData = {
        id: null,
        tempId: null,
        name: "",
        position_x: 0,
        position_y: 0,
        model_type: ConnectionSequenceStepModelType.IncomingPostback,
        model_id: postback.id || "",
        connection_sequence_step_next_id: null,
        connection_sequence_step_verify_phone_id: null,
        meanings: [],
        requestConfig: {
            id: "",
            name: "",
            method: RequestMethod.Get,
            url: "",
            timeout_ms: 0,
            send_payload_as: RequestPayloadType.Json,
            empty_fields_behavior: null,
            created_at: null,
            updated_at: null,
            offerConnectionSequenceSteps: [],
            requestConnectionProperties: [],
            responseHandlers: [],
            requestConnectionPropertiesUrl: [],
            headers: [],
        },
        type: null,
        wait_ms: null,
        created_at: null,
        updated_at: null,
    };

    if (!getConnectionSequenceStepsQuery.data?.connectionSequenceSteps) {
        return null;
    }

    const defaultValues: FormData = {
        first_sequence_step_id: postback.first_sequence_step_id,
        // responseConnectionPropertiesQuery:
        //     postback?.responseConnectionProperties?.filter(
        //         (prop) => prop.source === ResponsePropertySourceType.Query
        //     ) || [],

        // responseConnectionPropertiesBody:
        //     postback?.responseConnectionProperties?.filter((prop) => prop.source === ResponsePropertySourceType.Body) ||
        //     [],

        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        responseConnectionProperties: postback?.responseConnectionProperties,

        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        methods: getConnectionSequenceStepsQuery.data.connectionSequenceSteps.map((step) => ({
            ...step,
            tempId: step.id,
        })),
    };

    const onSubmit = async (values: FormData) => {
        if (!values.methods) {
            return;
        }

        const updatedValues = [...values.methods];

        const mutationPromises = updatedValues.map((method) => {
            const currentId = method.tempId;

            // TODO: rewrite for uuid; now type of if is used;
            // string - id from be
            // number - temporary id on fe

            if (deletedIds.includes(currentId)) {
                if (typeof currentId === "string") {
                    return destroyConnectionSequenceStepMutation.mutateAsync(currentId);
                } else {
                    return Promise.resolve();
                }
            } else {
                if (typeof currentId === "string") {
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    const updateMethod: StoreConnectionSequenceStepRequest = {
                        ...method,
                        model_type: ConnectionSequenceStepModelType.IncomingPostback,
                        model_id: postback.id,
                        meanings: method?.meanings?.map((meaning) => {
                            return typeof meaning === "string"
                                ? { id: null, connection_sequence_step_id: null, type: meaning }
                                : meaning;
                        }),
                        requestConfig: transformRequestConfig(method.requestConfig as App.Data.RequestConfigData),
                    };

                    return updateConnectionSequenceStepMutation.mutateAsync(updateMethod);
                } else {
                    const storeMethod: StoreConnectionSequenceStepRequest = {
                        ...method,
                        model_type: ConnectionSequenceStepModelType.IncomingPostback,
                        model_id: postback.id,
                        type: "send-request",
                        meanings: method?.meanings?.map((meaning) => {
                            return typeof meaning === "string"
                                ? { id: null, connection_sequence_step_id: null, type: meaning }
                                : meaning;
                        }),
                        requestConfig: transformRequestConfig(method.requestConfig as App.Data.RequestConfigData),
                    };

                    return storeConnectionSequenceStepMutation.mutateAsync(storeMethod);
                }
            }
        });

        try {
            for (const promise of mutationPromises) {
                await promise;
            }

            const res = await getConnectionSequenceStepsQuery.refetch();
            const newPostback = await updateIncomingPostbackMutation.mutateAsync(
                {
                    first_sequence_step_id: values.first_sequence_step_id,
                },
                {
                    onSuccess: () => {
                        queryClient.refetchQueries({ queryKey: ["getIncomingPostback", postback.id] });
                    },
                }
            );

            const newMethods = res.data?.connectionSequenceSteps as ConnectionSequenceStepData[];

            setDeletedIds([]);
            setLastTempId(0);

            toast.success(t("messages.methods_saved"));

            return {
                first_sequence_step_id: newPostback.incomingPostback.first_sequence_step_id,
                methods: newMethods.map((step) => ({
                    ...step,
                    tempId: step.id,
                })),
            };
        } catch (error) {
            console.error(error);
            return null;
        }
    };

    return (
        <NestedGeneralFormProvider<FormData>
            schema={schema}
            defaultValues={defaultValues}
            id={FormId.TrackingToolFromEditorForm}
            onSubmit={onSubmit}
        >
            <FormContent
                postback={postback}
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                emptyConnectionSequenceStepData={emptyConnectionSequenceStepData}
                deletedIds={deletedIds}
                setDeletedIds={setDeletedIds}
                lastTempId={lastTempId}
                setLastTempId={setLastTempId}
            />
        </NestedGeneralFormProvider>
    );
};

interface FormContentProps {
    postback: IncomingPostbackDataOptional;
    emptyConnectionSequenceStepData: ConnectionSequenceStepData;
    deletedIds: Array<number | string>;
    setDeletedIds: React.Dispatch<React.SetStateAction<Array<number | string>>>;
    lastTempId: number;
    setLastTempId: React.Dispatch<React.SetStateAction<number>>;
}

const FormContent: FC<FormContentProps> = ({
    lastTempId,
    setDeletedIds,
    emptyConnectionSequenceStepData,
    setLastTempId,
    deletedIds,
    postback,
}) => {
    const { control } = useFormContext<FormData>();
    const { fields, append } = useFieldArray({ control, name: "methods" });
    const permissionsUtils = usePermission();
    const { t } = usePaldockTranslation("ui.connection-creator-editor");

    const modifyIncomingPostbackMethodsDrawerId = DrawerId.ModifyPostbackIncomingMethods;

    const handleAddMethod = () => {
        setLastTempId((prevState) => prevState + 1);
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        append({ ...emptyConnectionSequenceStepData, tempId: lastTempId });
    };

    const handleRemoveMethod = (tempId: string | number) => {
        setDeletedIds((prev) => [...prev, tempId]);
    };

    return (
        <div>
            <Flex justify="space-between" className="mb-5">
                <Typography.Title level={5}>{t("sections.methods")}</Typography.Title>
                {permissionsUtils.hasPermission(PermissionEnum.ToolTrackingIncomingPostbackEditorExportView) && (
                    <DrawerLink
                        drawerId={modifyIncomingPostbackMethodsDrawerId}
                        action="show"
                        search={{
                            [`${modifyIncomingPostbackMethodsDrawerId}-id`]: postback.id ?? "",
                        }}
                    >
                        <Button type="primary">{"</>"}</Button>
                    </DrawerLink>
                )}
            </Flex>

            <IncomingPostbackHeaderPanel />

            {fields.map((field, index) => {
                const disabled = deletedIds.includes(field.tempId);
                return (
                    <ApiMethodPanel
                        key={field.id}
                        name={index}
                        disabled={disabled}
                        handleRemoveMethod={() => handleRemoveMethod(field.tempId)}
                        postback={postback}
                    />
                );
            })}

            <Row className="mt-3">
                <Col span={8} xs={4}>
                    <Button type="primary" onClick={handleAddMethod}>
                        {t("actions.add_new_method")}
                    </Button>
                </Col>
            </Row>
            <EditorDrawers />
        </div>
    );
};
